import React from "react";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle,
  MapPin,
  Sparkles,
  ThumbsUp,
  XCircle,
} from "lucide-react";
import { Bar, Doughnut } from "react-chartjs-2";

const DriverAnalyticsSection = ({
  reports,

  deliveryComparisonData,
  deliveryPieData,

  selectedStatus,
  setSelectedStatus,

  onTimeTasks,
  lateTasks,
  notDeliveredTasks,

  onTimeCount,
  lateCount,
  notDeliveredCount,
  normalCount,
  instantCount,
  pickupMissedCount,
  restroMissedCount,

  statusBasedReports,
  currentAnalyticsReports,

  analyticsPage,
  setAnalyticsPage,
  analyticsPerPage,

  selectedTask,
  setSelectedTask,

  showModal,
  setShowModal,

  showLogs,
  setShowLogs,

  assignmentLogs,
  setAssignmentLogs,

  statusChangeLogs,
  setStatusChangeLogs,

  fetchTaskLogs,

  fromDate,
  toDate,

  formatDistance,
  renderWaitingTimeCell,

  StatusCard,
  TablePagination,
  TaskDetailsModal,
}) => {
  if (!reports || reports.length === 0) {
    return (
      <div className="rounded-2xl border bg-white p-8 text-center text-gray-500">
        No analytics found for selected date range.
      </div>
    );
  }

  return (
    <>
      {/* CHARTS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* BAR CHART */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-lg font-semibold mb-4">
            Estimated vs Actual Delivery Time
          </h3>

          <div className="h-80">
            <Bar
              data={deliveryComparisonData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
              }}
            />
          </div>
        </div>

        {/* PERFORMANCE DOUGHNUT */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-lg font-semibold mb-4">
            Delivery Performance Distribution
          </h3>

          <div className="flex items-center justify-between h-80">
            <div className="w-1/2 h-full">
              <Doughnut
                key={JSON.stringify(deliveryPieData)}
                data={deliveryPieData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: "65%",
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                }}
              />
            </div>

            <div className="w-1/2 h-full overflow-y-auto pr-2 space-y-4 custom-scroll">
              {/* POSITIVE SECTION */}
              <div>
                <h4 className="text-sm font-semibold text-emerald-700 mb-2">
                  Positive Performance
                </h4>

                <div className="space-y-2">
                  <div className="relative h-10">
                    <div className="absolute left-0 top-0 h-full w-1 bg-emerald-600 rounded-l-md" />

                    <div className="bg-emerald-50 border border-emerald-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                      <span className="text-sm font-medium text-emerald-700">
                        On Time
                      </span>

                      <span className="text-lg font-semibold text-emerald-700">
                        {onTimeCount}
                      </span>
                    </div>
                  </div>

                  <div className="relative h-10">
                    <div className="absolute left-0 top-0 h-full w-1 bg-red-600 rounded-l-md" />

                    <div className="bg-red-50 border border-red-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                      <span className="text-sm font-medium text-red-700">
                        Late
                      </span>

                      <span className="text-lg font-semibold text-red-700">
                        {lateCount}
                      </span>
                    </div>
                  </div>

                  <div className="relative h-10">
                    <div className="absolute left-0 top-0 h-full w-1 bg-indigo-600 rounded-l-md" />

                    <div className="bg-indigo-50 border border-indigo-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                      <span className="text-sm font-medium text-indigo-700">
                        Normal
                      </span>

                      <span className="text-lg font-semibold text-indigo-700">
                        {normalCount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* NEGATIVE SECTION */}
              <div>
                <h4 className="text-sm font-semibold text-red-700 mb-2">
                  Miscellaneous / Violations
                </h4>

                <div className="space-y-2">
                  <div className="relative h-10">
                    <div className="absolute left-0 top-0 h-full w-1 bg-amber-600 rounded-l-md" />

                    <div className="bg-amber-50 border border-amber-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                      <span className="text-sm font-medium text-amber-700">
                        Driver Location Off
                      </span>

                      <span className="text-lg font-semibold text-amber-700">
                        {notDeliveredCount}
                      </span>
                    </div>
                  </div>

                  <div className="relative h-10">
                    <div className="absolute left-0 top-0 h-full w-1 bg-orange-600 rounded-l-md" />

                    <div className="bg-orange-50 border border-orange-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                      <span className="text-sm font-medium text-orange-700">
                        Instant Delivery
                      </span>

                      <span className="text-lg font-semibold text-orange-700">
                        {instantCount}
                      </span>
                    </div>
                  </div>

                  <div className="relative h-10">
                    <div className="absolute left-0 top-0 h-full w-1 bg-violet-600 rounded-l-md" />

                    <div className="bg-violet-50 border border-violet-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                      <span className="text-sm font-medium text-violet-700">
                        Pickup Missed
                      </span>

                      <span className="text-lg font-semibold text-violet-700">
                        {pickupMissedCount}
                      </span>
                    </div>
                  </div>

                  <div className="relative h-10">
                    <div className="absolute left-0 top-0 h-full w-1 bg-rose-600 rounded-l-md" />

                    <div className="bg-rose-50 border border-rose-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                      <span className="text-sm font-medium text-rose-700">
                        Restro Missed
                      </span>

                      <span className="text-lg font-semibold text-rose-700">
                        {restroMissedCount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* STATUS CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6 w-full">
        <StatusCard
          title="On Time"
          value={onTimeTasks.length}
          active={selectedStatus === "ontime"}
          onClick={() => setSelectedStatus("ontime")}
          icon={<ThumbsUp className="h-5 w-5 text-green-600" />}
          color={{
            border: "border-green-500",
            bg: "bg-green-50",
            text: "text-green-600",
            iconBg: "bg-green-100",
          }}
        />

        <StatusCard
          title="Late"
          value={lateTasks.length}
          active={selectedStatus === "late"}
          onClick={() => setSelectedStatus("late")}
          icon={<ArrowDown className="h-5 w-5 text-red-600" />}
          color={{
            border: "border-red-500",
            bg: "bg-red-50",
            text: "text-red-600",
            iconBg: "bg-red-100",
          }}
        />

        <StatusCard
          title="Normal Delivery"
          value={normalCount}
          active={selectedStatus === "normal"}
          onClick={() => setSelectedStatus("normal")}
          icon={<CheckCircle className="h-5 w-5 text-blue-700" />}
          color={{
            border: "border-blue-600",
            bg: "bg-blue-50",
            text: "text-blue-700",
            iconBg: "bg-blue-100",
          }}
        />

        <StatusCard
          title="Driver Location Off"
          value={notDeliveredTasks.length}
          active={selectedStatus === "notdelivered"}
          onClick={() => setSelectedStatus("notdelivered")}
          icon={<MapPin className="h-5 w-5 text-amber-700" />}
          color={{
            border: "border-amber-600",
            bg: "bg-amber-50",
            text: "text-amber-700",
            iconBg: "bg-amber-100",
          }}
        />

        <StatusCard
          title="Instant Delivery"
          value={instantCount}
          active={selectedStatus === "instant"}
          onClick={() => setSelectedStatus("instant")}
          icon={<Sparkles className="h-5 w-5 text-orange-600" />}
          color={{
            border: "border-orange-500",
            bg: "bg-orange-50",
            text: "text-orange-600",
            iconBg: "bg-orange-100",
          }}
        />

        <StatusCard
          title="Pickup Missed"
          value={pickupMissedCount}
          active={selectedStatus === "pickupmissed"}
          onClick={() => setSelectedStatus("pickupmissed")}
          icon={<ArrowUp className="h-5 w-5 text-violet-600" />}
          color={{
            border: "border-violet-500",
            bg: "bg-violet-50",
            text: "text-violet-600",
            iconBg: "bg-violet-100",
          }}
        />

        <StatusCard
          title="Restro Missed"
          value={restroMissedCount}
          active={selectedStatus === "restromissed"}
          onClick={() => setSelectedStatus("restromissed")}
          icon={<XCircle className="h-5 w-5 text-red-700" />}
          color={{
            border: "border-red-700",
            bg: "bg-red-100",
            text: "text-red-700",
            iconBg: "bg-red-200",
          }}
        />
      </div>

      {/* SELECTED STATUS TITLE */}
      <div className="mt-6 mb-3">
        <h2 className="text-lg font-semibold">
          {selectedStatus === "ontime" && "On Time Deliveries"}
          {selectedStatus === "late" && "Late Deliveries"}
          {selectedStatus === "notdelivered" && "Not Delivered Orders"}
          {selectedStatus === "locationoff" && "Driver Location Off Cases"}
          {selectedStatus === "instant" && "Instant Deliveries"}
          {selectedStatus === "pickupmissed" && "Pickup Missed"}
          {selectedStatus === "restromissed" && "Restro Missed"}
          {selectedStatus === "normal" && "Normal Deliveries"}
        </h2>

        <p className="text-sm text-gray-500">
          Showing {statusBasedReports.length} records
        </p>
      </div>

      {/* ANALYTICS TABLE */}
      <div className="bg-white border rounded-lg shadow-sm mt-4">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-orange-400 text-white text-left">
              <tr>
                <th className="px-4 py-2">Task Id</th>
                <th className="px-4 py-2">Order Id</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2">Actual Time</th>
                <th className="px-4 py-2">Estimate</th>
                <th className="px-4 py-2">Distance</th>
                <th className="px-4 py-2">Delivery Date</th>
                <th className="px-4 py-2">Waiting</th>
              </tr>
            </thead>

            <tbody>
              {currentAnalyticsReports.map((task, i) => (
                <tr key={task.pk_task_id || i} className="border-t">
                  <td
                    className="px-4 py-2 text-blue-600 cursor-pointer hover:underline"
                    onClick={() => {
                      setSelectedTask(task);
                      setShowModal(true);
                    }}
                  >
                    {task.pk_task_id}
                  </td>

                  <td className="px-4 py-2">{task.order_id}</td>

                  <td className="px-4 py-2">{task.task_description}</td>

                  <td className="px-4 py-2 d-flex align-items-center justify-center">
                    {task.actual_estimated_time
                      ? `${task.actual_estimated_time} min`
                      : "N/A"}
                  </td>

                  <td className="px-4 py-2">{task.estimated_time}</td>

                  <td className="px-4 py-2">
                    {formatDistance(task.distance)}
                  </td>

                  <td className="px-4 py-2">{task.delivery_date}</td>

                  <td className="px-4 py-2">
                    {renderWaitingTimeCell(task.waiting_time)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TablePagination
        currentPage={analyticsPage}
        totalCount={statusBasedReports.length}
        pageSize={analyticsPerPage}
        onPageChange={setAnalyticsPage}
      />

      <TaskDetailsModal
        show={showModal}
        task={selectedTask}
        onClose={() => {
          setShowModal(false);
          setShowLogs(false);
        }}
        showLogs={showLogs}
        setShowLogs={setShowLogs}
        assignmentLogs={assignmentLogs}
        statusChangeLogs={statusChangeLogs}
        onFetchLogs={async (taskId) => {
          const data = await fetchTaskLogs(taskId, fromDate, toDate);

          setAssignmentLogs(data?.assignmentLogs || []);
          setStatusChangeLogs(data?.statusChangeLogs || []);
        }}
      />
    </>
  );
};

export default DriverAnalyticsSection;